import "./mainBlock.scss";
import { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { pluralize } from "../helpers/pluralize";
import { ucFirst } from "../helpers/usFirst";
import { priceToUserString, priceToUserStringExact } from "../../../http/basketApi";
import { ReactComponent as MinusIcon } from "../../../assets/icons/minus.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import LikeNew from "../../../components/UI/LikeNew/LikeNew";
import clsx from "clsx";

// An unpublished canonical line has no product card. It can only be removed
// through the Store's existing owner-bound mutation and native snapshot ACK.
export const UnavailableBasketItem = observer(({basket, item}) => {
  const busy = useRef(false), mounted = useRef(true);
  const [pending, setPending] = useState(false), [error, setError] = useState('');
  useEffect(() => () => {mounted.current = false;}, []);
  const remove = async () => {
    if (busy.current || basket.snapshotLocked) return;
    busy.current = true; setPending(true); setError('');
    try {await basket.removeItem(item);}
    catch {if (mounted.current) setError('Не удалось удалить товар. Обновите корзину.');}
    finally {busy.current = false; if (mounted.current) setPending(false);}
  };
  return <div className="basketItem basketItem--unavailable" role="group" aria-label="Недоступный товар">
    <span>Товар недоступен</span>
    <button type="button" aria-label="Удалить недоступный товар" disabled={pending || basket.snapshotLocked} onClick={remove}>Удалить</button>
    {error && <span role="status">{error}</span>}
  </div>;
});

const MainBlock = observer(({ basket, handleDeleteBasket }) => {
  const [pendingDeletions, setPendingDeletions] = useState({});

  const deletionTimers = useRef({});
  const rowKey = item => basket.snapshotMode === 'enforced' ? item.lineKey : item.item.id;
  const activeKeys = new Set(basket.activeBasketItems.map(item => item.lineKey));
  const rows = basket.snapshotMode === 'enforced' ? basket.basketItems.filter(item => item.status === 'unavailable' || activeKeys.has(item.lineKey)) : basket.basketItems;
  const formatPrice = basket.snapshotMode === 'enforced' ? priceToUserStringExact : priceToUserString;
  const word = pluralize(basket.totalCountBasketItems, [
    "товар",
    "товара",
    "товаров",
  ]);

  const getPrice = (item) => {
    const price = basket.pricingLine?.(item);
    if (price) return price.basePrice * price.count;
    if (item.mbPerformance) {
      return (
        item.mbPerformance[0].basePricePerItem * item.mbPerformance[0].quantity
      );
    }
    return item.item.product.price * item.count;
  };

  const isDiscount = (item) => {
    const price = basket.pricingLine?.(item);
    if (price) return price.discount > 0;
    if (item.mbPerformance) {
      return (
        item.mbPerformance[0].basePricePerItem *
          item.mbPerformance[0].quantity >
        item.mbPerformance[0].discountedPriceOfLine
      );
    }
    return !!item.item.sell_price;
  };

  const getDiscount = (item) => {
    const price = basket.pricingLine?.(item);
    if (price) return price.lineTotal;
    if (item.mbPerformance) {
      return item.mbPerformance[0].discountedPriceOfLine;
    }
    return item.item.sell_price * item.count;
  };

  const changeCountAuthController = (modifier, item) => {
    basket.chenageCountItem(modifier, item);
  };

  const deleteAuthItemController = (item) => {

    const itemId = rowKey(item);

    setPendingDeletions((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    const timer = setTimeout(() => {
        if (window.turboTarget && window.turboTarget.sendRTGMessage) {
            window.turboTargetPixelRTGDelBasket(item.item.productId);
        } else {
            document.addEventListener("generalPixelInited", () => {
                window.turboTargetPixelRTGDelBasket(item.item.productId)
            })
        }
      basket.removeItem(item);
      setPendingDeletions((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      delete deletionTimers.current[itemId];
    }, 3000);

    deletionTimers.current[itemId] = timer;
  };

  const cancelDeletion = (item) => {
    const itemId = rowKey(item);

    clearTimeout(deletionTimers.current[itemId]);
    delete deletionTimers.current[itemId];

    setPendingDeletions((prev) => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
  };

  return (
    <div className={"mainBlock"}>
      <div className={"mainBlock__header"}>
        <h4 className={"mainBlock__title"}>Корзина</h4>
        <span className={"mainBlock__count"}>
          {basket.totalCountBasketItems} {word}
        </span>
        <div onClick={handleDeleteBasket} className={"mainBlock__delete"}>
          <span className={"mainBlock__delete-text"}>Очистить</span>
          <DeleteIcon />
        </div>
      </div>
      <div>
        {rows.length
          ? rows.map((item) => {
              if (basket.snapshotMode === 'enforced' && item.status === 'unavailable') return <UnavailableBasketItem key={item.lineKey} basket={basket} item={item}/>;
              const favoriteItem =
                basket.favoriteItems?.find((fav) => fav.id === item.item.id) ||
                {};
              return (
                <div className={"basketItem"}
                  key={rowKey(item)}
                >
                  <img
                    className={"basketItem__img"}
                    src={
                      process.env.REACT_APP_API_URL +
                      "uploads/images/" +
                      item.item.product.id +
                      "/" +
                      item.item.id +
                      "/" +
                      "214_" +
                      item.item.img_preview
                    }
                    alt={"YouWanna - " + (item.item.name ? item.item.name : "")}
                  />
                  <div className={"basketItem__price"}>
                    {basket.snapshotMode === 'enforced' && item.selected !== false && !basket.pricingLine(item) ? (
                      <span className="basketItem__price-current">Цена уточняется</span>
                    ) : <>
                    {isDiscount(item) && (
                      <span className="basketItem__price-old">
                        {formatPrice(getPrice(item)) + " ₽"}
                      </span>
                    )}
                    <span className="basketItem__price-current">
                      {isDiscount(item)
                        ? formatPrice(getDiscount(item)) + " ₽"
                        : formatPrice(getPrice(item)) + " ₽"}
                    </span>
                    </>}
                  </div>
                  <div className={"basketItem__text"}>
                    <span className={"basketItem__text-name"}>
                      {ucFirst(item.item.name)}
                    </span>
                    <span className={"basketItem__text-variant"}>
                      {item.currentSize} · {item.item.color.color_name}
                    </span>
                  </div>
                  <div className={"basketItem__count"}>
                    <MinusIcon
                      onClick={(e) =>
                        changeCountAuthController("decrease", item)
                      }
                    />
                    <div className={"basketItem__count-text"}>{item.count}</div>
                    <PlusIcon
                      onClick={(e) =>
                        changeCountAuthController("increase", item)
                      }
                    />
                  </div>
                  <div className={"basketItem__controls"}>
                    <div className={"basketItem__button"}>
                      <LikeNew
                        itemProduct={{
                          ...favoriteItem,
                          id: favoriteItem.product?.childProducts[0].id,
                          color: favoriteItem.product?.childProducts[0].color,
                        }}
                      />
                    </div>
                    <div
                      onClick={(e) => deleteAuthItemController(item)}
                      className={"basketItem__button"}
                    >
                      <DeleteIcon />
                    </div>
                  </div>
                  <div
                    className={clsx("basketItem__footer", {
                      "basketItem__footer--remove":
                        pendingDeletions[rowKey(item)],
                    })}
                  >
                    {pendingDeletions[rowKey(item)] && (
                      <>
                        <span className={"basketItem__footer-text"}>
                          Товар удален
                        </span>
                        <div
                          className={"basketItem__footer-button"}
                          onClick={() => cancelDeletion(item)}
                        >
                          <span>Вернуть</span>
                          <svg>
                            <rect
                              x="0"
                              y="0"
                              width="100%"
                              height="100%"
                              rx="6"
                              ry="6"
                            />
                          </svg>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          : null}
      </div>
    </div>
  );
});

export default MainBlock;
